import { Container, Sprite } from 'pixi.js';
import { GAME } from '@/config/game';
import { effectsProfile } from '@/config/fx';
import { getTexture, makeSprite } from '@/assets/resolver';
import type { PatternRunner } from '@/patterns/emitter';
import type { PatternDef } from '@/patterns/dsl';
import type { BulletSystem } from '@/entities/bullets';
import type { MinionKind } from '@/entities/minions';
import type { TelegraphSink } from '@/render/telegraphs';
import { BossMotion } from './boss-motion';
import type { BossDef, PhaseDef } from './boss-def';
import {
  levelHpMul, rampedRunners, tierPatterns, tierRamp, unlocked, type TierRamp,
} from './boss-tier';
import { LOOK, bodyTint, crackFrame } from './boss-look';
import { setCircle, type BossEvents, type HitCircle } from './boss-hooks';
import { SummonGate } from './summon-gate';

export { bossTier, levelDamageMul, levelHpMul } from './boss-tier';
export type { BossDef, BossMovement, PhaseDef } from './boss-def';

/**
 * BossDef interpreter: phases, pattern runners, signature movement,
 * damage/death. Bosses with bespoke behavior subclass this (docs/02).
 */
export class Boss {
  x: number;
  y: number;
  prevX: number;
  prevY: number;
  hp: number;
  maxHp: number;
  dead = false;
  /** Arena size (wired by run/boss-spawn.ts) — sweeps and walls stay inside it */
  arenaW: number = GAME.arena.width;
  arenaH: number = GAME.arena.height;

  readonly view = new Container();
  protected readonly body: Sprite;
  protected readonly ring: Sprite | null = null;
  private cracks!: Sprite;
  protected runners: PatternRunner[] = [];
  protected phaseIndex = -1;
  protected hitFlash = 0;
  protected moveAngle = 0;
  protected speedMul = 1;
  /** Tier ramp (docs/02): tempo, bullet speed, movement, tint */
  protected readonly ramp: TierRamp;
  protected readonly tier: number;
  /** Fire-rate multiplier — divide attack intervals by it */
  protected readonly intensity: number;
  protected readonly bulletSpeedMul: number;
  protected readonly levelSpeedMul: number;
  protected readonly levelTint: number;
  private readonly motion: BossMotion;

  // Host hooks — wired once per fight by combat/boss-hooks.ts
  onPhaseEnter: ((phase: PhaseDef) => void) | null = null;
  /** Counts only minions THIS boss summoned */
  minionCount: (() => number) | null = null;
  spawnMinions: ((kind: MinionKind, count: number) => void) | null = null;
  /** Bespoke events (part deaths, splits, slams) → host fx/shake/sfx/gems */
  events: BossEvents | null = null;
  /** The part the last explosion hurt — its damage number anchors there */
  readonly aoeAt: HitCircle = { x: 0, y: 0, r: 0 };

  // Surprise reinforcements at 50% HP (docs/12 E5): the boss is untouchable
  // until the elite squad dies. Bosses with bespoke minion shields opt out.
  protected allowReinforce = true;
  private reinforced = false;
  private readonly reinforceGate = new SummonGate();

  constructor(
    readonly def: BossDef,
    readonly level: number,
    spawnX: number,
    spawnY: number,
    protected readonly homeX: number,
    protected readonly homeY: number,
  ) {
    this.x = this.prevX = spawnX;
    this.y = this.prevY = spawnY;
    this.maxHp = Math.round(def.baseHp * levelHpMul(level));
    this.hp = this.maxHp;

    this.ramp = tierRamp(level);
    this.tier = this.ramp.tier;
    this.intensity = this.ramp.tempo;
    this.bulletSpeedMul = this.ramp.bulletSpeed;
    this.levelSpeedMul = this.ramp.move;
    this.levelTint = this.ramp.tint;
    this.motion = new BossMotion(def.movement, level, this.ramp.jink);

    this.body = makeSprite(def.bodyKey);
    if (def.ringKey) {
      this.ring = makeSprite(def.ringKey);
      this.view.addChild(this.ring);
    }
    this.view.addChild(this.body);

    // Damage-stage cracks overlay (docs/02: bosses visibly degrade)
    this.cracks = makeSprite('fx/boss-cracks', 0);
    this.cracks.visible = false;
    this.cracks.scale.set((def.radius * 2) / LOOK.crackSize);
    this.view.addChild(this.cracks);
  }

  get hpPct(): number {
    return Math.min(1, Math.max(0, this.hp / this.maxHp));
  }

  /** False while a mechanic blocks all damage (auto-aim retargets minions). */
  get isVulnerable(): boolean {
    return !this.reinforceGate.locked;
  }

  /** True while the reinforcement squad shields the boss (bespoke hit code). */
  protected get damageLocked(): boolean {
    return this.reinforceGate.locked;
  }

  /** Where auto-aim/homing should point — multi-part bosses return a part. */
  aimPoint(_px: number, _py: number): { x: number; y: number } {
    return this;
  }

  setArena(width: number, height: number): void {
    this.arenaW = width;
    this.arenaH = height;
  }

  /** Post-spawn HP scaling — bosses keep up with the player's firepower. */
  scaleHp(mul: number): void {
    this.maxHp = Math.round(this.maxHp * mul);
    this.hp = this.maxHp;
    this.scaleParts(mul);
  }

  /** Multi-part bosses scale EACH part with maxHp so parts still sum to it (X1). */
  protected scaleParts(_mul: number): void {}

  /** Tier-gated + tier-ramped runners; `withEvolve` adds BossDef.evolve. */
  protected makeRunners(patterns: ReadonlyArray<PatternDef>, withEvolve = false): PatternRunner[] {
    const list = withEvolve
      ? tierPatterns(patterns, this.def.evolve, this.phaseIndex, this.tier)
      : patterns.filter((p) => unlocked(p, this.tier));
    return rampedRunners(list, this.ramp);
  }

  /** Runs `runners` from the boss reference point (bespoke bosses too). */
  protected fireRunners(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    for (const r of this.runners) r.update(dt, this.x, this.y, px, py, bullets, telegraphs);
  }

  /** Any aimed volley winding up — read by the body cue in render(). */
  protected get telegraphing(): boolean {
    for (const r of this.runners) if (r.warning) return true;
    return false;
  }

  protected checkPhase(): void {
    const phases = this.def.phases;
    let target = this.phaseIndex;
    for (let i = 0; i < phases.length; i++) {
      if (this.hpPct <= phases[i]!.hpPct) target = i;
    }
    if (target !== this.phaseIndex && target >= 0) {
      this.phaseIndex = target;
      const phase = phases[target]!;
      // Signature attacks always; tier stacks the extras on top (docs/02)
      this.runners = this.makeRunners(phase.patterns, true);
      this.speedMul = phase.speedMul ?? 1;
      this.onPhaseEnter?.(phase);
    }
  }

  /** Half-HP elite squad on deep levels (gate drops on death or failsafe). */
  protected updateReinforce(dt: number): void {
    const run = GAME.run;
    if (
      this.allowReinforce && !this.reinforced &&
      this.level >= run.reinforceMinLevel &&
      this.hpPct <= run.reinforceHpPct &&
      this.spawnMinions !== null && this.minionCount !== null
    ) {
      this.reinforced = true;
      this.reinforceGate.raise();
      const size = run.reinforceBase + Math.floor(this.tier / run.reinforcePerTiers);
      this.spawnMinions('elite', Math.min(run.reinforceMax, size));
    }
    this.reinforceGate.update(dt, this.minionCount?.() ?? 0);
  }

  update(dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink): void {
    if (this.dead) return;
    this.prevX = this.x;
    this.prevY = this.y;
    this.hitFlash -= dt;

    this.checkPhase();
    this.updateReinforce(dt);
    this.motion.jink(this, dt, px, py);
    this.move(dt, px, py);
    this.fireRunners(dt, px, py, bullets, telegraphs);
  }

  /** Signature movement step — bespoke movers (Bastion, Stalker, …) override. */
  protected move(dt: number, px: number, py: number): void {
    this.motion.step(this, this.homeX, this.homeY, dt, px, py, this.speedMul * this.levelSpeedMul);
  }

  /** Player bullets vs the body circle (multi-part bosses override); blocked
   *  bullets still die ("clink"). @returns hits + damage dealt */
  absorbBullets(bullets: BulletSystem): { hits: number; dealt: number } {
    const hpBefore = this.hp;
    const hits = bullets.collideCircle(true, this.x, this.y, this.def.radius, (dmg) => this.landHit(dmg));
    return { hits, dealt: hpBefore - Math.max(0, this.hp) };
  }

  /** takeDamage reporting what really landed — damage numbers show exactly it. */
  protected landHit(amount: number): number {
    const before = Math.max(0, this.hp);
    this.takeDamage(amount);
    return before - Math.max(0, this.hp);
  }

  /** Hook: the boss landed a hit on the player (Kraken lifesteal — C7). */
  onDealtDamage(_amount: number): void {}

  /** Force on the SHIP (px/s) — Tide, Gale, Avatar, Magnet, Singularity; null = none. */
  environmentalForce(_px: number, _py: number): { x: number; y: number } | null {
    return null;
  }

  /** Ship speed multiplier at a point (Chronos time-warp zones); 1 = none. */
  playerSpeedMul(_px: number, _py: number): number {
    return 1;
  }

  /** Area-damage overlap test (mortar/rocket). */
  hitByAoe(x: number, y: number, radius: number): boolean {
    return Math.hypot(this.x - x, this.y - y) < radius + this.def.radius;
  }

  /** Explosion damage (X4/X5): multi-part bosses route it to the part INSIDE
   *  the blast and mark it. @returns damage actually dealt (0 while immune) */
  applyAoe(x: number, y: number, radius: number, damage: number): number {
    if (this.dead || !this.hitByAoe(x, y, radius)) return 0;
    this.markAoe(this.x, this.y, this.def.radius);
    return this.landHit(damage);
  }

  /** The explosion number anchors on this hit circle (multi-part: the part). */
  protected markAoe(x: number, y: number, r: number): void { setCircle(this.aoeAt, x, y, r); }

  /** Contact-damage test against the player. */
  touchesPlayer(px: number, py: number, playerRadius: number): boolean {
    return Math.hypot(this.x - px, this.y - py) < this.def.radius + playerRadius;
  }

  /** @returns true → the boss died from this hit */
  takeDamage(amount: number): boolean {
    if (this.dead) return false;
    this.hitFlash = LOOK.hitFlash;
    // Armor "clink" — the reinforcement squad must die first
    if (this.reinforceGate.locked) return false;
    this.hp -= amount;
    if (this.hp <= 0) {
      this.dead = true;
      return true;
    }
    return false;
  }

  render(alpha: number): void {
    this.view.position.set(
      this.prevX + (this.x - this.prevX) * alpha,
      this.prevY + (this.y - this.prevY) * alpha,
    );
    const punch = this.hitFlash > 0 ? effectsProfile().bossPunch : 1;
    // Aimed volleys wind up in the body: it swells and glows (X13)
    const warn = this.telegraphing;
    this.body.scale.set(punch * (warn ? LOOK.warnSwell : 1));
    // Damage stages: body darkens/reddens and cracks grow as hp drops
    this.body.tint = bodyTint(this.hpPct, this.levelTint, this.motion.windup > 0, warn);
    const frame = crackFrame(this.hpPct);
    this.cracks.visible = frame !== null;
    if (frame !== null) {
      this.cracks.texture = getTexture('fx/boss-cracks', frame);
      this.cracks.scale.set(((this.def.radius * 2) / LOOK.crackSize) * punch);
    }
    if (this.ring) {
      this.ring.rotation += LOOK.ringSpin * (warn ? LOOK.warnRingSpin : 1);
      this.ring.scale.set(punch);
    }
  }
}
